import React, {useEffect, useState} from "react";
import {Button, Input, message, Radio, Space, Spin, Tooltip, Typography, Upload} from "antd";
import {UploadOutlined} from "@ant-design/icons";
import {doc, getDoc, setDoc} from "firebase/firestore";
import {getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject} from "firebase/storage";
import {db} from "../config/firebase/firebase";
import {useAuth} from "../contexts/authContext";
import {CustomCvPage} from "./CustomCvPage";
import {setUserId} from "../config/amplitude/amplitude";
import {useNavigate} from "react-router-dom";

const {TextArea} = Input;
const {Title, Text} = Typography;

const CvConfigPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [option, setOption] = useState("a");
    const [coverLetterText, setCoverLetterText] = useState("");
    const [aiCoverLetterPrompt, setAiCoverLetterPrompt] = useState("");
    const [cvFile, setCvFile] = useState(null);
    const [cvFileList, setCvFileList] = useState([]);
    const [cvFileName, setCvFileName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [isFileRemoved, setIsFileRemoved] = useState(false); // Track if the file was removed
    const {currentUser} = useAuth();
    const storage = getStorage();
    const defaultPrompt = "напиши короткий (до 500 символів) cover letter українською який починається з привітання на основі мого резюме так щоб показати чому я good fit для цієї вакансії. Використовуй чоловічий рід, булет поінти, кількість років досвіду по кожному пункту і call to action (в якості call to action задай 1 питання по процесах проекту, які може знати рекрутер) без наворотій вкінці речення. У якості результаті мені потрібен лише текст cover letter без твоїх коментарів на початку і вкінці, без смайликів (можуть бути смайлик який злегка посміхається лише вкінці)";

    useEffect(() => {
        document.title = 'CV Config - CV Postman App';
        localStorage.setItem('lastOpenedPage', '/job-config/cv-config');
        setUserId(currentUser.email);
        const fetchData = async () => {
            setLoading(true);
            try {
                if (!currentUser) return;
                const userEmail = currentUser.email;
                const docRef = doc(db, `users/${userEmail}/cv.config/1`);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setCoverLetterText(data.coverLetterText || "");
                    setAiCoverLetterPrompt(data.aiCoverLetterPrompt || "");
                    setOption(getOption(data.type));
                    if (data.cvFileUrl && data.cvFileName) {
                        setCvFileName(data.cvFileName);
                        setCvFileList([
                            {
                                uid: "-1",
                                name: data.cvFileName,
                                status: "done",
                                url: data.cvFileUrl,
                            },
                        ]);
                        setIsFileRemoved(false); // Reset to false when file is preloaded
                    }
                } else {
                    console.log("No such document!");
                }
            } catch (err) {
                console.error("Error fetching data:", err);
                message.error("Failed to load data. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentUser]);

    const handleOptionChange = (e) => {
        setOption(e.target.value);
        /*setCoverLetterText("");
        setCvFile(null);
        setCvFileName("");
        setIsFileRemoved(false);*/
    };

    const handleFileChange = (info) => {
        if (info.fileList.length > 0) {
            const selectedFile = info.fileList[0].originFileObj || info.fileList[0];
            setCvFile(selectedFile);
            setCvFileName(selectedFile.name);
            setCvFileList(info.fileList);
            setIsFileRemoved(false); // File is not removed if a new file is uploaded
        } else {
            setCvFile(null);
            setCvFileName("");
            setCvFileList([]);
        }
    };

    const handleFileRemove = async (file) => {
        if (!currentUser) return;
        const userEmail = currentUser.email;
        const fileRef = ref(storage, `cvs/${userEmail}/${file.name}`);

        try {
            await deleteObject(fileRef);
            message.success("File removed successfully!");
            setCvFile(null);
            setCvFileName("");
            setCvFileList([]);
            setIsFileRemoved(true); // Mark file as removed
        } catch (error) {
            console.error("Error removing file:", error);
            message.error("Failed to remove file. Please try again.");
        }
    };

    function getOption(type: string) {
        switch (type) {
            case "cover-letter-text":
                return "a";
            case "ai-cover-letter":
                return "b";
            case "cvmaker":
                return "c";
        }
    }

    function getType(option: string) {
        switch (option) {
            case "a":
                return "cover-letter-text";
            case "b":
                return "ai-cover-letter";
            case "c":
                return "cvmaker";
        }

    }

    const handleSave = async () => {
        setUploading(true);
        try {
            if (!currentUser) throw new Error("User is not authenticated");
            if (!coverLetterText.trim() && !aiCoverLetterPrompt.trim()) {
                message.error('Please enter a cover letter');
                return;
            }
            const userEmail = currentUser.email;
            const docRef = doc(db, `users/${userEmail}/cv.config/1`);
            let cvFileUrl = "";

            // If the user removed the file, set URL to empty; otherwise, use the preloaded file URL
            if (isFileRemoved) {
                cvFileUrl = ""; // Explicitly remove file URL if user clicked remove
            } else if (cvFile) {
                const storageRef = ref(storage, `cvs/${userEmail}/${cvFile.name}`);
                const uploadTask = uploadBytesResumable(storageRef, cvFile);

                await new Promise<void>((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        null,
                        (error) => reject(error),
                        () => resolve()
                    );
                });

                cvFileUrl = await getDownloadURL(uploadTask.snapshot.ref);
            } else if (cvFileList.length > 0) {
                // Use existing URL if the file was not removed
                cvFileUrl = cvFileList[0].url;
            }


            await setDoc(docRef, {
                isActive: true,
                type: getType(option),
                coverLetterText: coverLetterText,
                aiCoverLetterPrompt: aiCoverLetterPrompt,
                cvFileUrl: cvFileUrl || "",
                cvFileName: cvFileName || "",
            });

            message.success("Saved successfully!");
            //setTimeout(() => navigate('/job-config/dou-config'), 1300);
        } catch (err) {
            console.error("Error saving data:", err);
            message.error("Failed to save. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Spin spinning={loading}>
            <div style={{maxWidth: 600, margin: "0 auto"}}>
                <Title level={3}>Select CV Option</Title>
                <Radio.Group onChange={handleOptionChange} value={option}>
                    <Space direction="vertical">
                        <Tooltip title="5-30% Recruiter reply rate">
                            <Radio value="a">Static Cover Letter</Radio>
                        </Tooltip>
                        <Tooltip title="+25% Recruiter reply rate">
                            <Radio value="b">
                                AI Cover Letter
                            </Radio>
                        </Tooltip>
                        <Tooltip title="Comming soon">
                            <Radio value="c" disabled={true}>
                                AI generated CV
                            </Radio>
                        </Tooltip>
                    </Space>
                </Radio.Group>

                <div style={{marginTop: "20px"}}>
                    {(option === "a" || option === "b") && (
                        <>
                            {option === "a" && (
                                <>
                                    <Text>Cover letter text:</Text>
                                    <TextArea
                                        placeholder="Cover Letter - Enter text (max 800 characters)"
                                        maxLength={800}
                                        value={coverLetterText}
                                        onChange={(e) => setCoverLetterText(e.target.value)}
                                        rows={4}
                                    />
                                </>
                            )}
                                {option === "b" && (
                                    <>

                                        <div
                                            style={{display: "flex", justifyContent: "space-between", margin: "8px 0"}}>
                                            <Text>Cover Letter <b>Prompt</b>:</Text>
                                            <Button
                                                onClick={() => setAiCoverLetterPrompt(defaultPrompt)}
                                                type="primary"
                                                ghost
                                                size="small"
                                            >
                                                Paste Default Prompt
                                            </Button>
                                        </div>
                                        <TextArea
                                            placeholder="Cover Letter Prompt"
                                            maxLength={1800}
                                            style={{minHeight: 300}}
                                            value={aiCoverLetterPrompt}
                                            onChange={(e) => setAiCoverLetterPrompt(e.target.value)}
                                            rows={4}
                                        />

                                    </>
                                )}
                                <Upload
                                    fileList={cvFileList}
                                    beforeUpload={() => false}
                                    onChange={handleFileChange}
                                    onRemove={handleFileRemove}
                                    accept=".pdf,.doc,.docx"
                                    maxCount={1}
                                >
                                    <Button icon={<UploadOutlined/>} style={{marginTop: 16}}>
                                        Upload CV File
                                    </Button>
                                </Upload>

                                <Button
                                    type="primary"
                                    onClick={handleSave}
                                    loading={uploading}
                                    style={{marginTop: 16}}
                                    disabled={!cvFile && !isFileRemoved && !coverLetterText.trim()}
                                >
                                    Save
                                </Button>
                        </>
                    )}
                    {(option === "c") && (
                        <CustomCvPage/>
                    )}
                </div>
            </div>
        </Spin>
    );
};

export default CvConfigPage;
